import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Bed, Bath, Maximize, MessageCircle } from "lucide-react";
import PublicLayout from "../components/PublicLayout";
import { useI18n } from "../i18n/I18nContext";
import { api, formatPrice } from "../lib/api";
import { useSite, waLink } from "../context/SiteContext";

export default function PropertyDetail() {
    const { id } = useParams();
    const { t, lang } = useI18n();
    const { site } = useSite();
    const phones = site?.contact?.phones || [];
    const [p, setP] = useState(null);
    const [active, setActive] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/properties/${id}`)
            .then((r) => setP(r.data))
            .catch(() => setP(null))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <PublicLayout>
                <div className="max-w-7xl mx-auto px-4 py-32 text-center text-gray-500">Chargement…</div>
            </PublicLayout>
        );
    }

    if (!p) {
        return (
            <PublicLayout>
                <div className="max-w-7xl mx-auto px-4 py-32 text-center text-gray-500">
                    {t("properties.empty")}
                </div>
            </PublicLayout>
        );
    }

    const title = lang === "en" ? p.title_en || p.title_fr : p.title_fr;
    const description = lang === "en" ? p.description_en || p.description_fr : p.description_fr;
    const isRent = p.transaction === "rent";
    const images = p.images?.length ? p.images : ["https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200"];

    const waText = `Bonjour GAB'S SERVICES, je suis intéressé(e) par le bien : "${title}" (${formatPrice(p.price, p.currency)}).`;

    return (
        <PublicLayout>
            <section className="bg-brand-paper py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/biens"
                        className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase font-semibold text-brand-navy hover:text-brand-gold transition-colors"
                        data-testid="property-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> {t("properties.back")}
                    </Link>
                </div>
            </section>

            <section className="bg-brand-paper pb-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
                    {/* Gallery */}
                    <div className="lg:col-span-7" data-testid="property-gallery">
                        <div className="aspect-[16/10] bg-white overflow-hidden border border-gray-100">
                            <img src={images[active]} alt={title} className="w-full h-full object-cover" />
                        </div>
                        {images.length > 1 && (
                            <div className="mt-3 grid grid-cols-5 gap-3">
                                {images.map((src, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActive(i)}
                                        data-testid={`gallery-thumb-${i}`}
                                        className={`aspect-[4/3] overflow-hidden border transition-all ${i === active ? "border-brand-gold ring-2 ring-brand-gold/40" : "border-gray-200 hover:border-brand-gold/60"}`}
                                    >
                                        <img src={src} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Info */}
                    <aside className="lg:col-span-5">
                        <div className="bg-white border border-gray-100 p-8 sticky top-32">
                            <span
                                className={`inline-block px-3 py-1 text-[0.62rem] tracking-[0.22em] uppercase font-bold mb-4 ${isRent ? "bg-brand-emerald text-white" : "bg-brand-gold text-white"}`}
                            >
                                {isRent ? t("featured.for_rent") : t("featured.for_sale")}
                            </span>
                            <h1 className="font-display text-3xl text-brand-navy leading-tight">
                                {title}
                            </h1>
                            <div className="mt-3 flex items-center gap-1.5 text-sm text-gray-600">
                                <MapPin className="w-4 h-4 text-brand-emerald" />
                                {p.city}
                                {p.neighborhood ? `, ${p.neighborhood}` : ""}
                            </div>
                            <div className="mt-6 text-3xl font-display font-semibold text-brand-emerald">
                                {formatPrice(p.price, p.currency)}
                                {isRent ? (
                                    <span className="text-sm text-gray-500 font-medium"> {t("featured.per_month")}</span>
                                ) : null}
                            </div>
                            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                                {p.surface ? (
                                    <Stat icon={Maximize} value={`${p.surface} m²`} label={t("properties.surface")} />
                                ) : null}
                                {p.bedrooms ? (
                                    <Stat icon={Bed} value={p.bedrooms} label={t("properties.bedrooms")} />
                                ) : null}
                                {p.bathrooms ? (
                                    <Stat icon={Bath} value={p.bathrooms} label={t("properties.bathrooms")} />
                                ) : null}
                            </div>
                            <p className="mt-6 text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                                {description}
                            </p>
                            <div className="mt-8 space-y-3">
                                <a
                                    href={`/reservation?property_id=${encodeURIComponent(p.id)}&type=rental`}
                                    data-testid="property-booking-btn"
                                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-emerald hover:bg-brand-emerald-soft text-white text-[0.72rem] tracking-[0.2em] uppercase font-semibold transition-colors"
                                >
                                    {t("booking.nav")}
                                </a>
                                <a
                                    href={`/demande?property_id=${encodeURIComponent(p.id)}&property_title=${encodeURIComponent(title)}`}
                                    data-testid="property-quote-btn"
                                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.72rem] tracking-[0.2em] uppercase font-semibold transition-colors"
                                >
                                    {t("nav.quote")}
                                </a>
                                {phones.map((ph) => (
                                    <a
                                        key={ph.waNumber}
                                        href={waLink(ph.waNumber, waText)}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`property-wa-${ph.waNumber}`}
                                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe57] text-white text-[0.72rem] tracking-[0.2em] uppercase font-semibold transition-colors"
                                    >
                                        <MessageCircle className="w-4 h-4" /> WhatsApp {ph.display}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </section>
        </PublicLayout>
    );
}

function Stat({ icon: Icon, value, label }) {
    return (
        <div className="bg-brand-paper border border-gray-100 px-3 py-4">
            <Icon className="w-4 h-4 text-brand-gold mx-auto mb-2" strokeWidth={1.5} />
            <div className="text-brand-navy font-semibold">{value}</div>
            <div className="text-[0.6rem] tracking-[0.2em] uppercase text-gray-500 mt-0.5">{label}</div>
        </div>
    );
}
